/**
 * Ícones autorais da navegação — portados 1:1 do mock (design/handoff/pipeline/project/ui.jsx,
 * objeto `Icon`). Mesma assinatura dos ícones lucide (size/strokeWidth/className) para troca direta.
 * viewBox 24, stroke currentColor, caps/joins arredondados, igual ao `IconBase` do mock.
 */
import type { SVGProps } from 'react';

interface NavIconProps extends Omit<SVGProps<SVGSVGElement>, 'stroke'> {
  size?: number;
  strokeWidth?: number;
}

function IconBase({ size = 16, strokeWidth = 1.5, children, ...props }: NavIconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Início — casa (Portal Vega, Bloco 1, DEC-060). */
export function HomeIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 11l8-7 8 7" />
      <path d="M6 10v10h12V10" />
      <path d="M10 20v-6h4v6" />
    </IconBase>
  );
}

/** Investidores (RI) — skyline (Portal Vega, Bloco 1, DEC-060). */
export function InvestorsIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <path d="M3 12l3-3 4 3 2-2 2 2 4-3 3 3" />
      <path d="M6 9V6h4" />
      <path d="M18 9V6h-4" />
      <path d="M8 15l3 3 2-2 2 2 3-3" />
    </IconBase>
  );
}

/** Pipeline — três barras decrescentes (funil). */
export function PipelineIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <rect x="3" y="3" width="6" height="18" rx="1" />
      <rect x="11" y="3" width="6" height="12" rx="1" />
      <rect x="19" y="3" width="2" height="7" rx="1" />
    </IconBase>
  );
}

/** Tarefas — linhas com checks. */
export function TaskIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 6h10" />
      <path d="M4 12h10" />
      <path d="M4 18h7" />
      <path d="M16 11l2 2 4-4" />
      <path d="M16 17l2 2 4-4" />
    </IconBase>
  );
}

/** Originação — grafo de nós (sourcing). */
export function OriginIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 2v6" />
      <path d="M5 9l7-1 7 1" />
      <circle cx="6" cy="14" r="3" />
      <circle cx="18" cy="14" r="3" />
      <path d="M12 8v3" />
      <path d="M9 21h6" />
    </IconBase>
  );
}

/** Portfólio — pasta. */
export function FolderIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </IconBase>
  );
}

/** Dashboards — linha de tendência. */
export function DashIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <path d="M3 12l4-4 4 4 4-6 6 8" />
      <path d="M3 19h18" />
    </IconBase>
  );
}

/** Configurações — engrenagem. */
export function SettingsIcon(props: NavIconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </IconBase>
  );
}
